import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'

import { GetCampaignFragment } from '@/types/campaign'
import { useEndUsersIntegrationsClient } from '@/lib/api'
import { Button, DatePickerDemo } from '@alphabait/ui'
import { EndUsersUploadedError } from '@/types/common'

export default function OneCommunityThreeDrawsAndPrize({
  campaign,
  onComplete,
  setErrors,
  setShowModal,
}: {
  campaign: GetCampaignFragment
  onComplete: () => void
  setErrors: (errors?: EndUsersUploadedError[]) => void
  setShowModal: (show: boolean) => void
}) {
  const { t } = useTranslation()
  const integrationsClient = useEndUsersIntegrationsClient()

  const hasOneCommunityThreeDrawsAndPrizeIntegration = useMemo(
    () =>
      campaign?.campaignCategory === 'generic' &&
      campaign?.customer?.integrations?.some(
        (i: any) =>
          i?.type === 'ONE_COMMUNITY' &&
          i.features?.threeDrawsAndPrize?.enabled,
      ),
    [campaign?.campaignCategory, campaign?.customer?.integrations],
  )

  const [createdAt, setCreatedAt] = useState<string | undefined>(
    campaign.optionalSettings?.oneCommunity?.createdAt
      ? new Date(campaign.optionalSettings.oneCommunity.createdAt)
        .toISOString()
        .substring(0, 10)
      : undefined,
  )

  const fetchThreeDrawsMutation = useMutation({
    mutationFn: async (data: { campaignId: string; createdAt: string }) => {
      const response = await integrationsClient.getThreeDrawsAndPrize.query({
        params: { campaignId: data.campaignId },
        query: { createdAt: data.createdAt },
      })

      // Handle validation errors (400 responses)
      if (response.status === 400) {
        throw response.body
      }

      return response
    },
    onSuccess: () => {
      onComplete()
    },
    onError: (error: any) => {
      // Backend returns array of errors directly
      if (Array.isArray(error)) {
        setErrors(error)
        setShowModal(true)
        return
      }

      console.error('Error fetching three draws and prize:', error)
    },
  })

  if (!hasOneCommunityThreeDrawsAndPrizeIntegration) return <></>

  return (
    <div className="flex flex-row justify-between bg-ui-100 rounded-lg py-4 px-2 items-center">
      <div className="text-gray-200 text-sm">
        <>
          <h1 className="px-1 text-base font-bold">
            {t('api_integration.generic_header')}
          </h1>
          <p className="p-1 text-sm font-normal">
            {t('api_integration.generic_text_three_draws')}
          </p>
        </>
        <div className="w-1/3 grid">
          <DatePickerDemo
            value={createdAt}
            setValue={(dateString: string) => {
              setCreatedAt(dateString)
            }}
          />
        </div>
      </div>

      <Button
        onClick={() => {
          if (createdAt) {
            fetchThreeDrawsMutation.mutate({
              campaignId: campaign.id,
              createdAt,
            })
          }
        }}
        className="flex flex-row cursor-pointer text-white"
        disabled={fetchThreeDrawsMutation.isPending || !createdAt}
      >
        {fetchThreeDrawsMutation.isPending ? 'Loading...' : t('api_integration.sync')}
      </Button>
    </div>
  )
}
